/* @flow */

import clsx from 'clsx';
import React from 'react';

import type { ChannelMap } from '../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_PARENTAL_CONTROL } from '../../libs/netgemLibrary/v8/types/ParentalControl';

import { ParentalControlKind } from '../../components/modal/parentalControlModal/ParentalControlModal';
import { ItemLocationKind, type NETGEM_API_V8_FEED_ITEM, type NetgemApiV8ItemLocation } from '../../libs/netgemLibrary/v8/types/FeedItem';
import { type NETGEM_API_V8_PARENTAL_GUIDANCE, type NETGEM_API_V8_PARENTAL_GUIDANCE_RATING, ParentalGuidanceRatingSystem } from '../../libs/netgemLibrary/v8/types/ParentalGuidance';
import { channelIsAdult } from '../channel/helper';
import AccurateTimestamp from '../dateTime/AccurateTimestamp';
import { parseBoolean } from '../jsHelpers/parser';
import Localizer from '../localization';
import { StorageKeys } from '../localStorage/keys';
import LocalStorageManager from '../localStorage/localStorageManager';
import { Messenger, MessengerEvents } from '../messenger';
import ProfilesManager from '../profiles/profilesManager';
import { getParentalGuidanceFromItem } from '../ui/item/traverser';
import ParentalControlApi from './api';
import {
  DEFAULT_LOCK_PURCHASE,
  DEFAULT_LOCKED_CHANNEL_IDS,
  DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES,
  DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO,
  DEFAULT_MINIMUM_AGE_FOR_ADULT_TV_CONTENT,
  LOCK_CHECK_INTERVAL,
  OtpResponseKind,
  type ParentalControlAndETag,
  type ParentalControlExtendedItem,
  type ParentalControlItemState,
  ParentalControlLabels,
  ParentalControlLockReason,
  ParentalControlLockState,
  type ParentalControlPurchaseState,
  UNLOCK_DURATION,
} from './constsAndTypes';
import { countryCodeToRatingSystem, formatMinAge, stringToRatingSystem } from './helpers';

class ParentalControlManager {
  static #accountLockPurchase: boolean = DEFAULT_LOCK_PURCHASE;
  static #accountLockedChannelIds: Array<string> = DEFAULT_LOCKED_CHANNEL_IDS;
  static #accountMaxAgeAllowedForGames: number = DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES;
  static #accountMaxAgeAllowedForVideo: number = DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO;
  static #api: ParentalControlApi | null = null;
  static #channelList: ChannelMap = {};
  static #deviceLockPurchase: boolean;
  static #deviceLockedChannelIds: Array<string>;
  static #deviceMaxAgeAllowedForGames: number;
  static #deviceMaxAgeAllowedForVideo: number;
  static #eTag: string | null = null;
  static #hasDeviceRestrictions: boolean;
  static #hasPinCode: boolean = false;
  static #lockTimer: IntervalID | null = null;
  static #lockDate: number = 0;
  static #minimumAgeForAdultTVContent: number = DEFAULT_MINIMUM_AGE_FOR_ADULT_TV_CONTENT;
  static #ratingSystem: ParentalGuidanceRatingSystem = stringToRatingSystem(process.env.REACT_APP_PARENTAL_RATING_SYSTEM) ?? ParentalGuidanceRatingSystem.Default;
  static #state: ParentalControlLockState = ParentalControlLockState.NotInitialized;

  /*
   * Getters for private members
   */

  static get hasDeviceRestrictions(): boolean {
    return this.#hasDeviceRestrictions;
  }

  static get eTag(): string | null {
    return this.#eTag;
  }

  static get accountLockPurchase(): boolean {
    return this.#accountLockPurchase;
  }

  static set accountLockPurchase(lockPurchase: boolean): void {
    this.#accountLockPurchase = lockPurchase;
  }

  static get deviceLockPurchase(): boolean {
    return this.#deviceLockPurchase;
  }

  static get isFeatureDisabled(): boolean {
    return ParentalControlManager.#api === undefined || ParentalControlManager.#api === null;
  }

  static get hasGamesRatingFeature(): boolean {
    return parseBoolean(process.env.REACT_APP_PARENTAL_CONTROL_FEATURE_GAMES_RATING);
  }

  static get hasVideoRatingFeature(): boolean {
    return parseBoolean(process.env.REACT_APP_PARENTAL_CONTROL_FEATURE_VIDEO_RATING);
  }

  static get hasLockPurchaseFeature(): boolean {
    return parseBoolean(process.env.REACT_APP_PARENTAL_CONTROL_FEATURE_LOCK_PURCHASE);
  }

  static get hasLockedChannelsFeature(): boolean {
    return parseBoolean(process.env.REACT_APP_PARENTAL_CONTROL_FEATURE_LOCKED_CHANNELS);
  }

  static getMaxAgeAllowedForVideo(kind: ParentalControlKind): number {
    // Device level
    if (kind === ParentalControlKind.Device) {
      return ParentalControlManager.#deviceMaxAgeAllowedForVideo;
    }

    // Account level
    return ParentalControlManager.#accountMaxAgeAllowedForVideo;
  }

  static initializeApi = async (baseUrl: string, authenticationToken: string | null, countryCode: string | null, minimumAgeForAdultTVContent: number) => {
    if (countryCode !== null) {
      // Override rating system with DMS settings
      ParentalControlManager.#ratingSystem = countryCodeToRatingSystem(countryCode);
    }

    const deviceRestrictions = LocalStorageManager.loadObject(StorageKeys.DeviceRestrictions, null);
    if (deviceRestrictions === null) {
      ParentalControlManager.#hasDeviceRestrictions = false;
    } else {
      ParentalControlManager.#hasDeviceRestrictions = true;
      ParentalControlManager.#deviceLockPurchase = deviceRestrictions.lockPurchaseForDevice ?? DEFAULT_LOCK_PURCHASE;
      ParentalControlManager.#deviceLockedChannelIds = deviceRestrictions.lockedChannelIds ?? DEFAULT_LOCKED_CHANNEL_IDS;
      ParentalControlManager.#deviceMaxAgeAllowedForGames = deviceRestrictions.maxAgeAllowedForGames ?? DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES;
      ParentalControlManager.#deviceMaxAgeAllowedForVideo = deviceRestrictions.maxAgeAllowedForVideo ?? DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO;
    }

    ParentalControlManager.#minimumAgeForAdultTVContent = minimumAgeForAdultTVContent > -1 ? minimumAgeForAdultTVContent : DEFAULT_MINIMUM_AGE_FOR_ADULT_TV_CONTENT;

    ParentalControlManager.#api = new ParentalControlApi(baseUrl);
    await ParentalControlManager.#api.updateAuthenticationToken(authenticationToken);
    ParentalControlManager.lock();
  };

  static disable = (minimumAgeForAdultTVContent: number) => {
    ParentalControlManager.#minimumAgeForAdultTVContent = minimumAgeForAdultTVContent > -1 ? minimumAgeForAdultTVContent : DEFAULT_MINIMUM_AGE_FOR_ADULT_TV_CONTENT;
    ParentalControlManager.unlock(false);
  };

  static updateChannelList = (channelList: ChannelMap) => {
    ParentalControlManager.#channelList = channelList;
  };

  // Only case where reset() is called is when user logs out
  static reset = (): void => {
    ParentalControlManager.#accountLockPurchase = DEFAULT_LOCK_PURCHASE;
    ParentalControlManager.#accountLockedChannelIds = DEFAULT_LOCKED_CHANNEL_IDS;
    ParentalControlManager.#accountMaxAgeAllowedForGames = DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES;
    ParentalControlManager.#accountMaxAgeAllowedForVideo = DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO;
    ParentalControlManager.resetETag();
    ParentalControlManager.unlock(false);
  };

  static setAllRestrictions = (parentalControl: NETGEM_API_PARENTAL_CONTROL, isInitializing: boolean, eTag: ?string) => {
    const { lockedChannelIds, lockPurchase, maxAgeAllowedForGames, maxAgeAllowedForVideo } = parentalControl;

    ParentalControlManager.#accountLockPurchase = lockPurchase ?? DEFAULT_LOCK_PURCHASE;
    ParentalControlManager.#accountLockedChannelIds = lockedChannelIds ?? DEFAULT_LOCKED_CHANNEL_IDS;
    ParentalControlManager.#accountMaxAgeAllowedForGames = maxAgeAllowedForGames ?? DEFAULT_MAX_AGE_ALLOWED_FOR_GAMES;
    ParentalControlManager.#accountMaxAgeAllowedForVideo = maxAgeAllowedForVideo ?? DEFAULT_MAX_AGE_ALLOWED_FOR_VIDEO;
    ParentalControlManager.#eTag = eTag ?? null;

    if (isInitializing) {
      ParentalControlManager.lock();
    }

    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);
  };

  static getAllRestrictions = (): ParentalControlAndETag => {
    return {
      eTag: ParentalControlManager.#eTag,
      parentalControl: {
        lockedChannelIds: ParentalControlManager.#accountLockedChannelIds,
        lockPurchase: ParentalControlManager.#accountLockPurchase,
        maxAgeAllowedForGames: ParentalControlManager.#accountMaxAgeAllowedForGames,
        maxAgeAllowedForVideo: ParentalControlManager.#accountMaxAgeAllowedForVideo,
      },
    };
  };

  static getAllRestrictionsWithNewMaxAgeAllowedForVideo = (maxAgeAllowedForVideo: number): ParentalControlAndETag => {
    const { eTag, parentalControl } = ParentalControlManager.getAllRestrictions();
    parentalControl.maxAgeAllowedForVideo = maxAgeAllowedForVideo;

    return { eTag, parentalControl };
  };

  static getAllRestrictionsWithNewLockPurchase = (lockPurchase: boolean): ParentalControlAndETag => {
    const { eTag, parentalControl } = ParentalControlManager.getAllRestrictions();
    parentalControl.lockPurchase = lockPurchase;

    return { eTag, parentalControl };
  };

  static resetETag = (): void => {
    ParentalControlManager.#eTag = null;
  };

  static setMaxAgeAllowedForVideo = (kind: ParentalControlKind, age: number) => {
    if (kind === ParentalControlKind.Device) {
      // Device level
      ParentalControlManager.#deviceMaxAgeAllowedForVideo = age;
      ParentalControlManager.storeDeviceRestrictions();
    } else {
      // Account level
      ParentalControlManager.#accountMaxAgeAllowedForVideo = age;
    }

    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);
  };

  static setMaxAgeAllowedForGames = (kind: ParentalControlKind, age: number) => {
    if (kind === ParentalControlKind.Device) {
      // Device level
      ParentalControlManager.#deviceMaxAgeAllowedForGames = age;
      ParentalControlManager.storeDeviceRestrictions();
    } else {
      // Account level
      ParentalControlManager.#accountMaxAgeAllowedForGames = age;
    }

    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);
  };

  static setDeviceLockPurchase = (lockPurchase: boolean) => {
    ParentalControlManager.#deviceLockPurchase = lockPurchase;
    ParentalControlManager.storeDeviceRestrictions();

    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);
  };

  // Check PIN code existence
  static updateHasPinCode = async (): Promise<void> => {
    const pinCodeStatus = await ParentalControlManager.#api?.getPinCodeStatus();

    if (!pinCodeStatus) {
      return;
    }

    const { contact, hasPinCode } = pinCodeStatus;

    if (!contact) {
      // Entirely disable parental control
      ParentalControlManager.#api = null;
    }
    ParentalControlManager.#hasPinCode = hasPinCode;
  };

  static onNewAuthToken: () => Promise<void> = ParentalControlManager.updateHasPinCode;

  static hasPinCode = (): boolean => ParentalControlManager.#hasPinCode;

  static checkPinCode = (pin: string): Promise<{ isCorrect: boolean, retryAfter: number, tooManyRequests?: boolean }> => {
    if (ParentalControlManager.#api === null) {
      return Promise.resolve({ isCorrect: false, retryAfter: 0 });
    }

    return ParentalControlManager.#api.checkPinCode(pin);
  };

  static askOtp = (): Promise<boolean> => {
    if (ParentalControlManager.#api === null) {
      return Promise.resolve(false);
    }

    return ParentalControlManager.#api.askOtp();
  };

  static createPinCode = async (otp: string, pin: string): Promise<OtpResponseKind> => {
    if (ParentalControlManager.#api === null) {
      return OtpResponseKind.Error;
    }

    const response = await ParentalControlManager.#api.setPinCode(otp, pin);

    if (response === OtpResponseKind.Success) {
      // Update PIN code status
      await ParentalControlManager.updateHasPinCode();

      // Notify all components
      Messenger.emit(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE);
    }

    return response;
  };

  static deletePinCode = async (otp: string): Promise<OtpResponseKind> => {
    if (ParentalControlManager.#api === null) {
      return OtpResponseKind.Error;
    }

    const response = await ParentalControlManager.#api.deletePinCode(otp);

    if (response === OtpResponseKind.Success) {
      // Update PIN code status
      await ParentalControlManager.updateHasPinCode();

      // Notify all components
      Messenger.emit(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE);
    }

    return response;
  };

  static getLabelElementFromParentalGuidance = (parentalGuidance: NETGEM_API_V8_PARENTAL_GUIDANCE | null, ratingSystem?: ParentalGuidanceRatingSystem): React.Node => {
    const labelAndMinimumAge = ParentalControlManager.getLabelAndMinimumAge(parentalGuidance, ratingSystem);
    if (labelAndMinimumAge === null) {
      return null;
    }

    const { ariaLabel, label, minimumAge } = labelAndMinimumAge;

    return (
      <div className={clsx('parentalGuidance', minimumAge > 0 && 'pictoInfo', ratingSystem === ParentalGuidanceRatingSystem.PEGI && `pegi${minimumAge}`)} key='parentalGuidance'>
        <span className='screenReaderOnly'>{ariaLabel}</span>
        <span aria-hidden>{label}</span>
      </div>
    );
  };

  // Return an HTML element containing the formatted label or null if there's no parental guidance
  static getLabelElementFromItem = (item: NETGEM_API_V8_FEED_ITEM): React.Node => {
    const parentalGuidance = getParentalGuidanceFromItem(item);
    const { locType } = item;

    return ParentalControlManager.getLabelElementFromParentalGuidance(parentalGuidance, locType === ItemLocationKind.Game ? ParentalGuidanceRatingSystem.PEGI : undefined);
  };

  static getItemState = (parentalGuidance: NETGEM_API_V8_PARENTAL_GUIDANCE | null, channelId?: string, locType: ?NetgemApiV8ItemLocation): ParentalControlItemState => {
    const hasPinCode = ParentalControlManager.hasPinCode();

    // 1. Adult channel has priority over everything else: content locked and can only be unlocked if parental control is enabled
    if (
      channelIsAdult(ParentalControlManager.#channelList, channelId) &&
      (locType === ItemLocationKind.ScheduledEvent || locType === ItemLocationKind.Catchup || locType === ItemLocationKind.Recording)
    ) {
      return {
        hasPinCode,
        lockReason: ParentalControlLockReason.AdultContent,
        lockState: ParentalControlLockState.Locked,
      };
    }

    // 2. Adult content is not allowed: content locked and can only be unlocked if parental control is enabled
    if (parentalGuidance?.adult === true) {
      return {
        hasPinCode,
        lockReason: ParentalControlLockReason.AdultContent,
        lockState: ParentalControlLockState.Locked,
      };
    }

    const labelAndMinimumAge = ParentalControlManager.getLabelAndMinimumAge(parentalGuidance);

    // 3. Missing parental guidance means content is not locked
    if (labelAndMinimumAge === null) {
      return { hasPinCode, lockState: ParentalControlLockState.Unlocked };
    }

    const { minimumAge } = labelAndMinimumAge;

    // 4.Content whose rating is higher than the minimum age for adult TV content: content locked and can only be unlocked if parental control is enabled
    if (
      minimumAge >= ParentalControlManager.#minimumAgeForAdultTVContent &&
      (locType === ItemLocationKind.ScheduledEvent || locType === ItemLocationKind.Catchup || locType === ItemLocationKind.Recording)
    ) {
      return {
        hasPinCode,
        lockReason: ParentalControlLockReason.AdultContent,
        lockState: ParentalControlLockState.Locked,
      };
    }

    // 5. Parental control disabled or video rating feature not available
    if (ParentalControlManager.isFeatureDisabled || !ParentalControlManager.hasVideoRatingFeature) {
      return {
        hasPinCode: false,
        lockState: ParentalControlLockState.Unlocked,
      };
    }

    // 6. Parental control still initializing
    if (ParentalControlManager.#state === ParentalControlLockState.NotInitialized) {
      return { hasPinCode, lockState: ParentalControlLockState.NotInitialized };
    }

    // 7. Parental control has been disabled by user
    if (ParentalControlManager.#state === ParentalControlLockState.Unlocked) {
      return { hasPinCode, lockState: ParentalControlLockState.Unlocked };
    }

    // 8. Check profile restriction, or device/account restriction
    const maxAgeAllowed = ProfilesManager.isEnabled
      ? ProfilesManager.getMaxAgeAllowedForVideoForCurrentProfile()
      : ParentalControlManager.#hasDeviceRestrictions
        ? ParentalControlManager.#deviceMaxAgeAllowedForVideo
        : ParentalControlManager.#accountMaxAgeAllowedForVideo;

    if (minimumAge > maxAgeAllowed) {
      // Access to this content is denied
      return { hasPinCode, lockReason: ParentalControlLockReason.VideoAge, lockState: ParentalControlLockState.Locked };
    }

    // 9. General case: content allowed
    return { hasPinCode, lockState: ParentalControlLockState.Unlocked };
  };

  // Item could be locked through rating (parental guidance) or channel
  static getItemStateFromItem = (item: NETGEM_API_V8_FEED_ITEM): ParentalControlItemState =>
    ParentalControlManager.getItemState(getParentalGuidanceFromItem(item), item.selectedLocation.channelId, item.locType);

  static getPurchaseState = (): ParentalControlPurchaseState => {
    if (ParentalControlManager.isFeatureDisabled || !ParentalControlManager.hasLockPurchaseFeature) {
      return {
        hasPinCode: false,
        lockState: ParentalControlLockState.Unlocked,
      };
    }

    const hasPinCode = ParentalControlManager.hasPinCode();

    if (ParentalControlManager.#state === ParentalControlLockState.NotInitialized) {
      return { hasPinCode, lockState: ParentalControlLockState.NotInitialized };
    }

    // Look for profile restriction (if enabled), or device/account restriction
    const lockPurchase = ProfilesManager.isEnabled
      ? ProfilesManager.getLockPurchaseForCurrentProfile()
      : ParentalControlManager.#hasDeviceRestrictions
        ? ParentalControlManager.#deviceLockPurchase
        : ParentalControlManager.#accountLockPurchase;

    const lockState = lockPurchase ? ParentalControlLockState.Locked : ParentalControlLockState.Unlocked;

    return { hasPinCode, lockState };
  };

  // Return the default label, as a string, or a "no restriction" string if there's no parental guidance
  static getDefaultLabel = (parentalGuidance: NETGEM_API_V8_PARENTAL_GUIDANCE): string => {
    if (parentalGuidance) {
      const { minimumAge } = parentalGuidance;
      const pg = Number(minimumAge);
      if (pg > 0) {
        return `-${pg}`;
      }
    }

    return Localizer.localize('common.metadata.no_age_restriction');
  };

  static getLabelAndMinimumAgeForRatingSystem = (
    ratings?: Array<NETGEM_API_V8_PARENTAL_GUIDANCE_RATING>,
    ratingSystem?: ParentalGuidanceRatingSystem,
  ): { label?: string, minimumAge?: number } | null => {
    const localRatingSystem = ratingSystem ?? ParentalControlManager.#ratingSystem;

    // Look for current rating system
    const matchingRatingSystem = ratings?.find((r) => r.system === localRatingSystem);

    if (!matchingRatingSystem) {
      // Current rating system not found
      return null;
    }

    const { label, minimumAge } = matchingRatingSystem;
    return { label, minimumAge };
  };

  static getLabelAndMinimumAge = (
    parentalGuidance: NETGEM_API_V8_PARENTAL_GUIDANCE | null,
    ratingSystem?: ParentalGuidanceRatingSystem,
  ): { ariaLabel: string, label: string, minimumAge: number } | null => {
    if (parentalGuidance === null) {
      return null;
    }

    // Default aria-label
    let ariaLabel = Localizer.localize('accessibility.content.rating.description_for_all_audiences');

    const { label: defaultLabel, minimumAge: defaultMinimumAge, ratings } = parentalGuidance;

    const rating = ParentalControlManager.getLabelAndMinimumAgeForRatingSystem(ratings, ratingSystem);

    const minimumAge = rating?.minimumAge ?? defaultMinimumAge;
    let label = rating?.label ?? defaultLabel;

    if (typeof label !== 'string') {
      // Fallback to legacy strategy for label
      label = ParentalControlManager.getDefaultLabel(parentalGuidance);

      if (minimumAge > 0) {
        ariaLabel = Localizer.localize('accessibility.content.rating.description_for_minium_age', { age: minimumAge });
      }
    } else if (minimumAge > 0) {
      ariaLabel = Localizer.localize('accessibility.content.rating.description_for_label', { rating: label });
    }

    return { ariaLabel, label, minimumAge };
  };

  static getFormattedMaxAgeAllowedForVideo = (): string => {
    // Look for profile restriction (if enabled), or device/account restriction
    const maxAgeAllowed = ProfilesManager.isEnabled
      ? ProfilesManager.getMaxAgeAllowedForVideoForCurrentProfile()
      : ParentalControlManager.#hasDeviceRestrictions
        ? ParentalControlManager.#deviceMaxAgeAllowedForVideo
        : ParentalControlManager.#accountMaxAgeAllowedForVideo;

    return formatMinAge(ParentalControlManager.#ratingSystem, maxAgeAllowed);
  };

  static getTextForCurrentMaxAgeAllowedForVideo = (kind?: ParentalControlKind, shortVersion: boolean = false): string => {
    // Look for profile restriction (if enabled), or device/account restriction
    const maxAgeAllowed = ProfilesManager.isEnabled
      ? ProfilesManager.getMaxAgeAllowedForVideoForCurrentProfile()
      : kind === ParentalControlKind.Device || (kind === undefined && ParentalControlManager.#hasDeviceRestrictions)
        ? ParentalControlManager.#deviceMaxAgeAllowedForVideo
        : ParentalControlManager.#accountMaxAgeAllowedForVideo;

    return shortVersion ? ParentalControlManager.getShortTextFromMinAge(maxAgeAllowed) : ParentalControlManager.getTextFromMinAge(maxAgeAllowed);
  };

  static getShortTextFromMinAge = (minAge: number): string => {
    const ratings = ParentalControlLabels[ParentalControlManager.#ratingSystem as string];
    const minAgeIndex = ratings.findIndex(({ minAge: age }) => age === minAge);

    if (minAgeIndex === -1) {
      // Not found: not supposed to happen
      return '';
    }

    if (minAgeIndex === ratings.length - 1) {
      // Lowest restriction
      return Localizer.localize('settings.parental_control.video_restriction.hint_short.min_age_18');
    }

    if (minAgeIndex === 0) {
      // Highest restriction
      return Localizer.localize('settings.parental_control.video_restriction.hint_short.min_age_0', { rating: formatMinAge(ParentalControlManager.#ratingSystem, minAge) });
    }

    // All other restrictions
    return Localizer.localize('settings.parental_control.video_restriction.hint_short.min_age_n', { rating: formatMinAge(ParentalControlManager.#ratingSystem, minAge) });
  };

  static getTextFromMinAge = (minAge: number): string => {
    const ratings = ParentalControlLabels[ParentalControlManager.#ratingSystem as string];
    const minAgeIndex = ratings.findIndex(({ minAge: age }) => age === minAge);

    if (minAgeIndex === -1) {
      // Not found: not supposed to happen
      return '';
    }

    if (minAgeIndex === ratings.length - 1) {
      // Lowest restriction
      return Localizer.localize('settings.parental_control.video_restriction.hint.min_age_18');
    }

    const nextMinAge = ratings[minAgeIndex + 1].minAge;

    // eslint-disable-next-line no-magic-numbers
    if (minAgeIndex === ratings.length - 2) {
      // Second to lowest restriction
      return Localizer.localize('settings.parental_control.video_restriction.hint.min_age_17', { rating: formatMinAge(ParentalControlManager.#ratingSystem, nextMinAge) });
    }

    // All other restrictions
    return Localizer.localize('settings.parental_control.video_restriction.hint.min_age_n', { rating: formatMinAge(ParentalControlManager.#ratingSystem, nextMinAge) });
  };

  static getRestrictionItems = (): Array<ParentalControlExtendedItem> =>
    ParentalControlLabels[ParentalControlManager.#ratingSystem as string].map(({ label, minAge }) => {
      return { label: label ?? formatMinAge(ParentalControlManager.#ratingSystem, minAge), minAge, text: ParentalControlManager.getTextFromMinAge(minAge) };
    });

  static isLocked = (): boolean => ParentalControlManager.#state === ParentalControlLockState.Locked;

  static lock = (): void => {
    ParentalControlManager.#state = ParentalControlLockState.Locked;
    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);
    ParentalControlManager.#lockDate = 0;
    clearInterval(ParentalControlManager.#lockTimer);
  };

  static unlock = (startTimer: ?boolean = true): void => {
    ParentalControlManager.#state = ParentalControlLockState.Unlocked;
    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);

    if (startTimer) {
      ParentalControlManager.#lockDate = AccurateTimestamp.now() + UNLOCK_DURATION;

      ParentalControlManager.#lockTimer = setInterval(() => {
        if (AccurateTimestamp.now() < ParentalControlManager.#lockDate) {
          // Lock date not reached yet
          return;
        }

        // Lock date reached
        ParentalControlManager.lock();
      }, LOCK_CHECK_INTERVAL);
    }
  };

  static lockPurchaseForDevice = (): void => {
    ParentalControlManager.setDeviceLockPurchase(true);
  };

  static unlockPurchaseForDevice = (): void => {
    ParentalControlManager.setDeviceLockPurchase(false);
  };

  static storeDeviceRestrictions = (): void => {
    // Store device restrictions locally
    LocalStorageManager.save(StorageKeys.DeviceRestrictions, {
      lockedChannelIds: ParentalControlManager.#deviceLockedChannelIds,
      lockPurchase: ParentalControlManager.#deviceLockPurchase,
      maxAgeAllowedForGames: ParentalControlManager.#deviceMaxAgeAllowedForGames,
      maxAgeAllowedForVideo: ParentalControlManager.#deviceMaxAgeAllowedForVideo,
    });
  };

  static setDeviceRestrictions = (): void => {
    // Copy account values to device
    ParentalControlManager.#deviceLockPurchase = ParentalControlManager.#accountLockPurchase;
    ParentalControlManager.#deviceLockedChannelIds = ParentalControlManager.#accountLockedChannelIds;
    ParentalControlManager.#deviceMaxAgeAllowedForGames = ParentalControlManager.#accountMaxAgeAllowedForGames;
    ParentalControlManager.#deviceMaxAgeAllowedForVideo = ParentalControlManager.#accountMaxAgeAllowedForVideo;

    ParentalControlManager.storeDeviceRestrictions();

    ParentalControlManager.#hasDeviceRestrictions = true;
    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);
  };

  static deleteDeviceRestrictions = (): void => {
    // Delete device restrictions locally
    LocalStorageManager.delete(StorageKeys.DeviceRestrictions);

    ParentalControlManager.#hasDeviceRestrictions = false;
    Messenger.emit(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE);
  };
}

export default ParentalControlManager;
